import { Component, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
    ControlValueAccessor,
    FormArray,
    FormControl,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR
} from '@angular/forms';
import { SelectOption } from '../../../../core/interfaces/select-option';
import { ControlComponent } from '../../control.component';
import { CostsControl } from '../costs-control.model';
import { CostsControlValue } from './costs-control-value.model';
import { CostsControlController } from './costs-control.controller';

@Component({
    selector: 'vas-costs-field',
    templateUrl: 'costs-control.component.html',
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CostsControlComponent), multi: true},
        {provide: NG_VALIDATORS, useExisting: forwardRef(() => CostsControlComponent), multi: true}
    ],
    viewProviders: [
        CostsControlController
    ]
})

export class CostsControlComponent extends ControlComponent implements ControlValueAccessor, OnInit, OnDestroy {
    @Input('value') _value: Array<CostsControlValue>;
    @Input() definition: CostsControl;
    @Input() formControl: FormControl;
    @Input() formGroup: FormGroup;
    @Input() assetId: string;
    @Output() changes: EventEmitter<any> = new EventEmitter();

    removeModeEnabled = false;

    constructor(
        protected elemRef: ElementRef,
        protected controller: CostsControlController
    ) {
        super(elemRef);

        this.form.valueChanges
            .takeUntil(this.ngUnsubscribe)
            .subscribe(value => {
                this.updateValue(value);
            });
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.controller.initialise(this.definition);

    }

    get costOptions(): Array<SelectOption> {
        return this.controller.costOptions;
    }

    get currency(): string {
        return this.controller.currency;
    }

    get currencyFormControl(): FormControl {
        return this.controller.currencyFormControl;
    }

    get currencyLabel(): string {
        return this.controller.currencyLabel;
    }

    get currencyOptions(): Array<SelectOption> {
        return this.controller.currencyOptions;
    }

    get form(): FormArray {
        return this.controller.form;
    }

    get netTotal(): string {
        return this.controller.netTotal;
    }

    get showLabels(): boolean {
        return this.controller.showLabels;
    }

    get total(): string {
        return this.controller.total;
    }

    get totalSavings(): string {
        return this.controller.totalSavings;
    }

    get totalNetSavings(): string {
        return this.controller.totalNetSavings;
    }

    get savingsVatTotal(): string {
        return this.controller.savingsVatTotal;
    }

    get totalEstimated(): string {
        return this.controller.totalEstimated;
    }

    get totalNetEstimated(): string {
        return this.controller.totalNetEstimated;
    }

    get estimatedVatTotal(): string {
        return this.controller.estimatedVatTotal;
    }

    get vatOptions(): Array<SelectOption> {
        return this.controller.vatOptions;
    }

    get vatDefault(): string | number {
        return this.controller.vatDefault;
    }

    get vatTotal(): string {
        return this.controller.vatTotal;
    }

    onRemove(i: number): void {
        this.controller.remove(i);
    }

    onChangeCurrency(value): void {
        this.controller.currencyFormControl.setValue(value);
    }

    onCreate(): void {
        this.controller.addItem({
            agreed: 0,
            currency: this.currency,
            estimated: 0,
            lookup: undefined,
            vatRate: this.vatDefault
        });
    }

    onReset(): void {
        this.controller.reset();
    }

    onSwitchRemoveMode(): void {
        this.removeModeEnabled = !this.removeModeEnabled;
    }

    writeValue(value?: any): void {
        super.writeValue(value);
        this.controller.writeValue(value);
    }

    getSaving(item: FormGroup): string {
        const estimated = item.get('estimated').value;
        const agreed = item.get('agreed').value;
        return this.controller.getSaving(estimated, agreed);
    }
}



// WEBPACK FOOTER //
// ./src/vas/controls/costs/form/costs-control.component.ts